import { formatEther, parseEther } from 'viem';
import type { Hex } from './demo/types';

/* ────────────────────────────────────────────────────────────────────────────
   Display formatting. Every number the user sees goes through this module so
   precision, grouping and sign conventions stay identical across the app.
   ──────────────────────────────────────────────────────────────────────────── */

/** ETH price used for fiat conversions. Swap for an oracle read when one exists. */
export const ETH_USD = 3120;

const nf = (min: number, max: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: min, maximumFractionDigits: max });

/** Compact magnitude suffix: 12.4K, 3.09M, 1.2B. */
export function compact(n: number, digits = 2): string {
  const abs = Math.abs(n);
  if (!Number.isFinite(n)) return '—';
  if (abs >= 1e12) return (n / 1e12).toFixed(digits) + 'T';
  if (abs >= 1e9) return (n / 1e9).toFixed(digits) + 'B';
  if (abs >= 1e6) return (n / 1e6).toFixed(digits) + 'M';
  if (abs >= 1e3) return (n / 1e3).toFixed(digits >= 2 ? 1 : digits) + 'K';
  if (abs >= 1) return nf(0, digits).format(n);
  if (abs === 0) return '0';
  return n.toPrecision(2);
}

/** ETH amount with a magnitude-aware number of decimals. */
export function eth(n: number, opts: { digits?: number; unit?: boolean } = {}): string {
  const { unit = true } = opts;
  const abs = Math.abs(n);
  const digits =
    opts.digits ??
    (abs >= 1000 ? 0 : abs >= 100 ? 1 : abs >= 1 ? 3 : abs >= 0.001 ? 4 : 6);
  if (abs > 0 && abs < 1e-6) return unit ? '<0.000001 ETH' : '<0.000001';
  const body = abs >= 10_000 ? compact(n, 2) : nf(0, digits).format(n);
  return unit ? `${body} ETH` : body;
}

/**
 * Fixed-decimal ETH for numeric table columns. `eth()` picks its precision by
 * magnitude and drops trailing zeros, so a value landing on a whole number
 * renders `8` beside `-1.2971` and breaks the column's decimal grid. Use this
 * wherever figures stack vertically.
 */
export function ethFixed(value: number, opts: { digits?: number; signed?: boolean } = {}): string {
  const { digits = 4, signed = false } = opts;
  if (!Number.isFinite(value)) return '—';
  const body = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
  return signed && value > 0 ? `+${body}` : body;
}

/** Same as `eth` but takes wei. */
export function ethFromWei(wei: bigint, opts?: { digits?: number; unit?: boolean }): string {
  return eth(Number(formatEther(wei)), opts);
}

export function weiToNumber(wei: bigint): number {
  return Number(formatEther(wei));
}

export function toWei(value: string | number): bigint {
  try {
    return parseEther(String(value || 0));
  } catch {
    return 0n;
  }
}

export function usd(n: number, opts: { digits?: number } = {}): string {
  const abs = Math.abs(n);
  if (abs >= 1000) return '$' + compact(n, 2);
  return '$' + nf(0, opts.digits ?? (abs >= 1 ? 2 : 4)).format(n);
}

export function ethAsUsd(ethAmount: number): string {
  return usd(ethAmount * ETH_USD);
}

/* ────────────────────────────────────────────────────────────────────────────
   Token prices.

   Launch prices span ~1e-3 to ~1e-10, so neither plain decimals (unreadably
   long) nor exponents (unscannable, and they break column alignment) work on
   their own. Both are also *inconsistent* if you switch between them by
   magnitude, which is what made the first pass look sloppy.

   The whole app instead uses one form: leading-zero-run notation, where the run
   of zeros after the decimal point is collapsed into a count —
   `0.000000210` renders as `0.0₆210`. `priceParts` is the structured version
   for the `<Price>` component; `priceSub` is the flat string for SVG labels and
   aria text.
   ──────────────────────────────────────────────────────────────────────────── */

/** Significant digits kept in every price, regardless of magnitude. */
const PRICE_SIG = 4;

export interface PriceParts {
  /** Text before the collapsed run, always `0.0` when `zeros > 0`. */
  lead: string;
  /** How many zeros were collapsed. `0` means render `lead` + `digits` plainly. */
  zeros: number;
  digits: string;
  negative: boolean;
}

export function priceParts(n: number): PriceParts {
  if (!Number.isFinite(n) || n === 0) {
    return { lead: '0', zeros: 0, digits: '', negative: false };
  }
  const negative = n < 0;
  const abs = Math.abs(n);

  if (abs >= 1) {
    return { lead: nf(2, 4).format(abs), zeros: 0, digits: '', negative };
  }

  const exp = Math.floor(Math.log10(abs));
  // Digits after the decimal point needed to keep PRICE_SIG significant figures.
  const decimals = Math.min(60, -exp + PRICE_SIG - 1);
  const fixed = abs.toFixed(decimals);
  const frac = fixed.slice(2);
  const zeros = frac.length - frac.replace(/^0+/, '').length;
  // Trailing zeros are kept deliberately: these land in mono tabular columns, and
  // trimming them made `0.0₈51` sit beside `0.0₈3542` with a ragged right edge.
  const digits = frac.slice(zeros) || '0';

  // Below three collapsed zeros the plain decimal is shorter and easier to read.
  if (zeros < 3) return { lead: `0.${'0'.repeat(zeros)}${digits}`, zeros: 0, digits: '', negative };
  return { lead: '0.0', zeros, digits, negative };
}

const SUBSCRIPTS = '₀₁₂₃₄₅₆₇₈₉';
const toSubscript = (n: number) =>
  String(n)
    .split('')
    .map((d) => SUBSCRIPTS[Number(d)] ?? d)
    .join('');

/** Flat string form of `priceParts`, for SVG text and `aria-label`s. */
export function priceSub(n: number): string {
  const { lead, zeros, digits, negative } = priceParts(n);
  const body = zeros > 0 ? `${lead}${toSubscript(zeros)}${digits}` : lead;
  return negative ? `-${body}` : body;
}

/**
 * Plain-decimal price with no exponent, for places that must stay ASCII
 * (CSV-ish output, test assertions). Prefer `<Price>` or `priceSub` in the UI.
 */
export function price(n: number): string {
  if (!Number.isFinite(n) || n === 0) return '0';
  const abs = Math.abs(n);
  if (abs >= 1) return nf(2, 4).format(n);
  const exp = Math.floor(Math.log10(abs));
  const decimals = Math.min(60, -exp + PRICE_SIG - 1);
  return (n < 0 ? '-' : '') + abs.toFixed(decimals);
}

/** Signed percent from a fraction (0.0412 → "+4.12%"). */
export function pct(fraction: number, opts: { digits?: number; sign?: boolean } = {}): string {
  const { digits = 2, sign = true } = opts;
  if (!Number.isFinite(fraction)) return '—';
  const v = fraction * 100;
  const body = nf(0, digits).format(Math.abs(v) < 0.005 ? 0 : v);
  return sign && v > 0 ? `+${body}%` : `${body}%`;
}

/** Unsigned percent for shares and progress (0.62 → "62%"). */
export function share(fraction: number, digits = 1): string {
  if (!Number.isFinite(fraction)) return '—';
  const v = Math.max(0, Math.min(1, fraction)) * 100;
  return `${nf(0, v >= 10 ? Math.min(digits, 1) : digits).format(v)}%`;
}

export function bps(value: number): string {
  return `${nf(0, 2).format(value / 100)}%`;
}

/** Token balance from raw units. */
export function tokens(raw: bigint, decimals = 18, digits = 2): string {
  const divisor = 10n ** BigInt(decimals);
  const whole = Number(raw / divisor);
  const frac = Number(raw % divisor) / Number(divisor);
  return compact(whole + frac, digits);
}

export function shortAddress(address?: string, chars = 4): string {
  if (!address) return '—';
  if (address.length <= chars * 2 + 2) return address;
  return `${address.slice(0, chars + 2)}…${address.slice(-chars)}`;
}

export function shortHash(hash?: string, chars = 6): string {
  return shortAddress(hash, chars);
}

/** "3m ago", "4h ago", "12 Aug". Stable, no dependency on a date library. */
export function timeAgo(ts: number, now = Date.now()): string {
  const s = Math.max(0, Math.floor((now - ts) / 1000));
  if (s < 5) return 'now';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  if (d < 365) return `${Math.floor(d / 7)}w`;
  return `${Math.floor(d / 365)}y`;
}

export function clockTime(ts: number): string {
  return new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(ts);
}

export function dateTime(ts: number): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(ts);
}

/** "2d 14h", used for countdowns and window timers. */
export function duration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${sec}s`;
  return `${sec}s`;
}

const EXPLORERS: Record<number, string> = {
  8453: 'https://basescan.org',
  84532: 'https://sepolia.basescan.org',
  4663: 'https://explorer.robinhood.com',
  46632: 'https://explorer-sepolia.robinhood.com',
};

export function explorerUrl(
  chainId: number,
  kind: 'tx' | 'address' | 'token',
  value: string,
): string {
  const root = EXPLORERS[chainId] ?? EXPLORERS[84532];
  const path = kind === 'tx' ? 'tx' : kind === 'token' ? 'token' : 'address';
  return `${root}/${path}/${value}`;
}

/** Deterministic hue from an address, so generated marks are stable. */
export function hueFromAddress(address: string): number {
  let h = 0;
  for (let i = 2; i < address.length; i++) h = (h * 31 + address.charCodeAt(i)) % 360;
  return h;
}

export function isAddress(value: string): value is Hex {
  return /^0x[0-9a-fA-F]{40}$/.test(value);
}
